import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#ece2cd] ${className}`} data-id="element-310">
      <div className="container mx-auto px-4" data-id="element-311">
        {title && <h2 className="font-black text-center mb-12 text-[#1e4230] text-[18px] md:text-2xl font-[Merriweather,_serif]" data-id="element-312">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-313">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white p-6 rounded-2xl shadow-[0_8px_24px_-12px_rgba(31,50,39,0.25)] border border-[#e0d6bf] flex flex-col h-full" data-id="element-314">
              <div className="flex items-center gap-1 mb-4" data-id="element-315">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#c8971b] fill-[#c8971b]" data-id="element-316" />)}
              </div>

              <p className="text-[#4a4133] italic mb-6 flex-grow leading-relaxed" data-id="element-317">
                "{t.text}"
              </p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#e0d6bf]" data-id="element-318">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-319">
                  {t.initials}
                </div>
                <div data-id="element-320">
                  <p className="font-bold text-[#1e4230] text-sm" data-id="element-321">{t.name}</p>
                  <p className="text-xs text-[#6f4e2e]" data-id="element-322">{t.location}</p>
                  {t.pack && <p className="text-xs font-semibold text-[#a97e12] mt-0.5" data-id="element-323">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'MR',
  name: 'María R.',
  location: 'Profesora de Ciencias Naturales · Secundaria · Córdoba',
  text: 'Llevaba años enseñando Geología con fotocopias sueltas que armaba de cero cada año. Abrí el Tomo 3 de Geología Estructural y en una tarde tenía más material contextualizado en Argentina que en toda mi carrera docente. Ahora mis alumnos me preguntan sobre los Andes y tengo respuesta para todo.',
  color: 'bg-[#2f6b47]'
}, {
  initials: 'JC',
  name: 'Julián C.',
  location: 'Docente de Geografía · Terciario · Neuquén',
  text: 'El Tomo 6 de Recursos Naturales y Geología Argentina me dio el sistema que necesitaba para conectar la materia con la realidad del país. Mis alumnos por primera vez entendieron por qué Argentina tiene litio, petróleo y volcanes. Lo implementé en dos semanas y los cambios en el aula fueron inmediatos.',
  color: 'bg-[#6f4e2e]'
}, {
  initials: 'SP',
  name: 'Sofía P.',
  location: 'Profesora de Ciencias de la Tierra · Formación Docente · Jujuy',
  text: 'Las Fichas Imprimibles del Banco de Actividades las uso en cada clase práctica. Mis alumnos trabajan con material profesional y eso genera un nivel de compromiso que nunca había visto. Nunca pensé que algo tan concreto iba a cambiar tanto mis clases.',
  color: 'bg-[#1e4230]'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'PM',
  name: 'Pablo M.',
  location: '🏙️ Buenos Aires',
  text: 'Dos años enseñando Geología con los mismos ejemplos importados de los libros. Con el Megapack en una tarde tenía el argumento pedagógico para cada proceso geológico con un ejemplo argentino concreto. Mis alumnos empezaron a preguntar más que nunca.',
  color: 'bg-[#2f6b47]',
  pack: 'Megapack Geología'
}, {
  initials: 'RL',
  name: 'Rosa L.',
  location: '🏙️ Santa Fe',
  text: 'El Tomo 5 de Geología Aplicada me dio el enfoque perfecto para conectar la materia con la realidad del país. Cuando explicás Vaca Muerta o el litio de la Puna, los alumnos se enganchan solos. Mis clases cambiaron completamente.',
  color: 'bg-[#6f4e2e]',
  pack: 'Megapack Geología'
}, {
  initials: 'GF',
  name: 'Gabriela F.',
  location: '🏙️ Tucumán',
  text: 'Con las Presentaciones para Clase empecé a proyectar material profesional todos los días sin quedarme sin ideas. En un mes mis alumnos preguntaban más, participaban más y los resultados de las evaluaciones mejoraron visiblemente.',
  color: 'bg-[#a97e12]',
  pack: 'Megapack + Nivel Experto'
}, {
  initials: 'AM',
  name: 'Andrés M.',
  location: '🏙️ Rosario',
  text: 'Cerré la planificación anual de Ciencias de la Tierra en dos horas usando los 6 Tomos como guía. Antes me llevaba semanas y siempre quedaba algo sin resolver. Ahora tengo todo el año organizado y contextualizado en Argentina.',
  color: 'bg-[#2f6b47]',
  pack: 'Megapack + Nivel Experto'
}, {
  initials: 'SV',
  name: 'Silvia V.',
  location: '🏙️ Córdoba',
  text: 'El Banco de Evaluaciones lo uso desde el primer día. Las preguntas con casos argentinos reales generan debates que antes nunca tenía en clase. Mis alumnos discuten sobre el litio, los glaciares y los terremotos como si fueran ciudadanos informados. Eso no tiene precio.',
  color: 'bg-[#1e4230]',
  pack: 'Megapack Geología'
}];